import type { TrackRecord } from "../types";
import { setDetailTitle } from "../meta";
import { escapeHtml } from "../util/html";
import { resolveAudioUrl } from "../util/audio";
import { fetchJson } from "../util/fetch-json";
import { deferForFastPaint } from "../util/defer";

const LECTURES_URL = "/data/music-lectures.json";
const TRACKS_URL = "/data/tracks-local.json";

export interface GuideFigure {
  name: string;
  nameEn?: string;
  years?: string;
  role?: string;
  bio: string;
  listenHint?: string;
  trackIds?: number[];
}

export interface GuideLecture {
  id: number;
  slug: string;
  status?: string;
  title: string;
  subtitle?: string;
  era?: string;
  yearRange?: string;
  coverImage?: string;
  coverCaption?: string;
  lead?: string;
  whyPair?: string;
  closing?: string;
  furtherReading?: string;
  figures: GuideFigure[];
}

function publishedLectures(list: GuideLecture[]): GuideLecture[] {
  return list.filter((l) => (l.status || "published") === "published");
}

export function renderGuides(): string {
  return `
    <div id="guides-root" class="guides-page">
      <p class="section-sub" style="margin-top:0">正在加载导读…</p>
    </div>
  `;
}

function lectureCard(l: GuideLecture): string {
  const names = l.figures.map((f) => f.name).join(" · ");
  const meta = [l.era, l.yearRange].filter(Boolean).join(" · ");
  const cover = l.coverImage
    ? `<div class="guide-card-cover"><img src="${escapeHtml(l.coverImage)}" alt="${escapeHtml(l.title)}" loading="lazy" decoding="async" width="960" height="540" /></div>`
    : "";
  return `
    <a class="guide-card" href="/guides?lecture=${l.id}">
      ${cover}
      <div class="guide-card-copy">
        <p class="guide-card-kicker">第 ${l.id} 讲</p>
        <h2 class="guide-card-title">${escapeHtml(l.title)}</h2>
        ${l.subtitle ? `<p class="guide-card-sub">${escapeHtml(l.subtitle)}</p>` : ""}
        <p class="guide-card-names">${escapeHtml(names)}</p>
        ${meta ? `<p class="guide-card-meta">${escapeHtml(meta)}</p>` : ""}
        ${l.lead ? `<p class="guide-card-lead">${escapeHtml(l.lead)}</p>` : ""}
      </div>
    </a>
  `;
}

function listView(lectures: GuideLecture[]): string {
  return `
    <header class="guides-page-head">
      <h1 class="section-title" style="margin-top:0">导读馆</h1>
      <p class="section-sub guides-page-lead">
        以一对音乐家对照着读、对照着听：短传记理清生平，站内名曲随手可播。
        文稿为尚诺艾原创；更多讲次将陆续上线。
      </p>
    </header>
    <div class="guide-card-list">
      ${lectures.map(lectureCard).join("")}
    </div>
  `;
}

function trackListenRow(t: TrackRecord): string {
  const meta = [t.composer, t.era, t.year != null ? String(t.year) : ""].filter(Boolean).join(" · ");
  const src = t.audioFile ? escapeHtml(resolveAudioUrl(t.audioFile)) : "";
  const player = src
    ? `<audio controls preload="none" src="${src}" class="guide-track-audio"></audio>`
    : "";
  return `
    <li class="guide-track-item">
      <a class="guide-track-main" href="/music?track=${t.id}">
        <span class="guide-track-title">${escapeHtml(t.title)}</span>
        <span class="guide-track-meta">${escapeHtml(meta)}</span>
      </a>
      ${player}
    </li>
  `;
}

function figureBlock(f: GuideFigure, byId: Map<number, TrackRecord>): string {
  const tracks = (f.trackIds || [])
    .map((id) => byId.get(id))
    .filter((t): t is TrackRecord => !!t);
  const listen = tracks.length
    ? `<ol class="guide-track-list">${tracks.map(trackListenRow).join("")}</ol>`
    : `<p class="section-sub">相关曲目稍后入库。</p>`;
  return `
    <article class="guide-figure">
      <header class="guide-figure-head">
        <h2 class="guide-figure-name">${escapeHtml(f.name)}</h2>
        <p class="guide-figure-meta">
          ${[f.nameEn, f.years, f.role].filter(Boolean).map((x) => escapeHtml(String(x))).join(" · ")}
        </p>
      </header>
      <p class="guide-figure-bio">${escapeHtml(f.bio)}</p>
      ${f.listenHint ? `<p class="guide-figure-hint">${escapeHtml(f.listenHint)}</p>` : ""}
      <h3 class="guide-listen-label">站内可听</h3>
      ${listen}
    </article>
  `;
}

function detailView(l: GuideLecture, tracks: TrackRecord[]): string {
  const byId = new Map(tracks.map((t) => [t.id, t]));
  const cover = l.coverImage
    ? `<figure class="guide-cover">
        <img src="${escapeHtml(l.coverImage)}" alt="${escapeHtml(l.coverCaption || l.title)}" width="1600" height="900" decoding="async" />
        ${l.coverCaption ? `<figcaption class="guide-cover-caption">${escapeHtml(l.coverCaption)}</figcaption>` : ""}
      </figure>`
    : "";
  return `
    <div class="detail-layout guide-detail">
      <a href="/guides" class="text-link back-link">← 返回导读馆</a>
      <p class="guide-detail-kicker">第 ${l.id} 讲${l.era ? ` · ${escapeHtml(l.era)}` : ""}</p>
      <h1 class="section-title" style="margin-top:0.35rem">${escapeHtml(l.title)}</h1>
      ${l.subtitle ? `<p class="detail-sub">${escapeHtml(l.subtitle)}${l.yearRange ? ` · ${escapeHtml(l.yearRange)}` : ""}</p>` : ""}
      ${cover}
      ${l.lead ? `<p class="detail-lead">${escapeHtml(l.lead)}</p>` : ""}
      ${
        l.whyPair
          ? `<h2 class="detail-heading">为何成对</h2><p class="detail-body">${escapeHtml(l.whyPair)}</p>`
          : ""
      }
      <div class="guide-figures">
        ${l.figures.map((f) => figureBlock(f, byId)).join("")}
      </div>
      ${
        l.closing
          ? `<h2 class="detail-heading">听完之后</h2><p class="detail-body">${escapeHtml(l.closing)}</p>`
          : ""
      }
      ${
        l.furtherReading
          ? `<p class="section-sub guide-further">${escapeHtml(l.furtherReading)}</p>`
          : ""
      }
    </div>
  `;
}

export function hydrateGuides(container: HTMLElement): void {
  const root = container.querySelector("#guides-root");
  if (!root) return;

  deferForFastPaint(() => {
    void (async () => {
      try {
        const [lecturesPayload, tracksPayload] = await Promise.all([
          fetchJson<{ lectures: GuideLecture[] }>(LECTURES_URL, "导读目录"),
          fetchJson<{ tracks: TrackRecord[] }>(TRACKS_URL, "曲库"),
        ]);
        if (!root.isConnected) return;

        const lectures = publishedLectures(lecturesPayload.lectures ?? []);
        const tracks = tracksPayload.tracks ?? [];
        const sp = new URLSearchParams(window.location.search);
        const lectureRaw = sp.get("lecture");
        const lectureId = lectureRaw ? Number(lectureRaw) : NaN;

        if (Number.isFinite(lectureId)) {
          const lecture = lectures.find((l) => l.id === lectureId);
          if (!lecture) {
            root.innerHTML = `<p class="section-sub">未找到第 ${escapeHtml(String(lectureId))} 讲。<a href="/guides">返回导读馆</a></p>`;
            return;
          }
          setDetailTitle(`${lecture.title}`);
          root.innerHTML = detailView(lecture, tracks);
          return;
        }

        root.innerHTML = listView(lectures);
      } catch (e) {
        if (!root.isConnected) return;
        const msg = e instanceof Error ? e.message : "加载失败";
        root.innerHTML = `<p class="section-sub">${escapeHtml(msg)}</p>`;
      }
    })();
  });
}
